(function () {
  var SECTION_SELECTOR = '.oddit-section-main-collection';
  var DESKTOP_QUERY = '(min-width: 990px)';
  var PRICE_DEBOUNCE_MS = 300;

  // ---------- Money ----------
  // Mirrors Shopify's money_format placeholders so the price label the slider
  // writes during a drag matches the one Liquid rendered on load.
  function groupDigits(cents, decimals, thousands, decimalSep) {
    var parts = (cents / 100).toFixed(decimals).split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts[1] ? whole + decimalSep + parts[1] : whole;
  }

  function formatMoney(cents, format) {
    return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount': return groupDigits(cents, 2, ',', '.');
        case 'amount_no_decimals': return groupDigits(cents, 0, ',', '.');
        case 'amount_with_comma_separator': return groupDigits(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return groupDigits(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return groupDigits(cents, 2, "'", '.');
        case 'amount_with_space_separator': return groupDigits(cents, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return groupDigits(cents, 0, ' ', '.');
        case 'amount_with_period_and_space_separator': return groupDigits(cents, 2, ' ', '.');
        default: return '';
      }
    });
  }

  function initSection(section) {
    // Delegated listeners survive the innerHTML swaps, so a second init would
    // double-bind rather than rebind.
    if (section.getAttribute('data-filters-init') === 'true') return;
    section.setAttribute('data-filters-init', 'true');

    var sectionId = section.getAttribute('data-section-id');
    var collectionUrl = section.getAttribute('data-collection-url') || window.location.pathname;
    var desktop = window.matchMedia(DESKTOP_QUERY);

    var sidebar = section.querySelector('[data-filter-sidebar]');
    var form = section.querySelector('[data-filter-form]');
    var content = section.querySelector('.oddit-section-main-collection__content');
    var applyBtn = section.querySelector('[data-filter-apply]');
    var clearBtn = section.querySelector('[data-filter-clear]');

    var controller = null;
    var requestSeq = 0;
    var priceTimer = null;
    // Mobile only: the fetched-but-not-yet-shown result, committed by Apply.
    var pending = null;
    // Mobile only: the form as it looked when the drawer opened, so closing
    // without applying discards the shopper's uncommitted checkboxes.
    var snapshot = null;

    // ---------- Fetch ----------
    function cancelInFlight() {
      if (controller) {
        try { controller.abort(); } catch (e) { /* already settled */ }
        controller = null;
      }
    }

    function setBusy(busy, quiet) {
      // A mobile preview must not dim the grid, which is partly visible behind
      // the drawer and is not what is being reloaded. The Apply button is.
      if (quiet) {
        if (applyBtn) applyBtn.setAttribute('aria-busy', busy ? 'true' : 'false');
        return;
      }
      if (busy) section.setAttribute('data-loading', 'true');
      else section.removeAttribute('data-loading');
    }

    // Resolves with the parsed section document, or null when the request was
    // superseded by a newer one (the seq token, not the abort, is what
    // guarantees an older response can never overwrite a newer render).
    function fetchSection(url, quiet) {
      var seq = ++requestSeq;
      cancelInFlight();
      controller = new AbortController();
      setBusy(true, quiet);

      var separator = url.indexOf('?') === -1 ? '?' : '&';
      return fetch(url + separator + 'section_id=' + encodeURIComponent(sectionId), {
        signal: controller.signal,
        credentials: 'same-origin'
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Filter request failed: ' + response.status);
          return response.text();
        })
        .then(function (text) {
          if (seq !== requestSeq) return null;
          setBusy(false, quiet);
          return new DOMParser().parseFromString(text, 'text/html');
        })
        .catch(function (error) {
          if (error.name === 'AbortError') return null;
          if (seq !== requestSeq) return null;
          setBusy(false, quiet);
          // Never strand the shopper on a stale grid — fall back to a real navigation.
          window.location.href = url;
          return null;
        });
    }

    // ---------- Params ----------
    function buildUrl() {
      var params = new URLSearchParams();
      if (form) {
        new FormData(form).forEach(function (value, key) {
          // `page` never survives a filter change: the new result set has its own paging.
          if (value === '' || key === 'page') return;
          params.append(key, value);
        });

        // A slider parked at its maximum is not an upper bound, so send no price param.
        var price = form.querySelector('[data-price-max-input]');
        if (price && price.value === price.getAttribute('data-price-range-max')) {
          params.delete(price.name);
        }
      }
      var query = params.toString();
      return collectionUrl + (query ? '?' + query : '');
    }

    // ---------- Render ----------
    function readAccordionState() {
      var state = {};
      if (!form) return state;
      form.querySelectorAll('.oddit-section-main-collection__filter-group').forEach(function (group) {
        var label = group.querySelector('[data-filter-heading] span');
        if (label) state[label.textContent.trim()] = group.getAttribute('data-open');
      });
      return state;
    }

    function applyAccordionState(state) {
      if (!form) return;
      form.querySelectorAll('.oddit-section-main-collection__filter-group').forEach(function (group) {
        var label = group.querySelector('[data-filter-heading] span');
        if (!label || state[label.textContent.trim()] !== 'false') return;
        group.setAttribute('data-open', 'false');
        var heading = group.querySelector('[data-filter-heading]');
        if (heading) heading.setAttribute('aria-expanded', 'false');
      });
    }

    // The form is replaced wholesale, so a keyboard user mid-arrow-key on the
    // slider would otherwise lose focus on every debounced apply.
    function readFocus() {
      var el = document.activeElement;
      if (!form || !el || !form.contains(el)) return null;
      if (el.matches('[data-price-max-input]')) return { price: true };
      return el.name ? { name: el.name, value: el.value } : null;
    }

    function restoreFocus(key) {
      if (!key || !form) return;
      var el = key.price
        ? form.querySelector('[data-price-max-input]')
        : form.querySelector('[name="' + CSS.escape(key.name) + '"][value="' + CSS.escape(key.value) + '"]');
      if (el) el.focus({ preventScroll: true });
    }

    function syncSortUi() {
      var input = form && form.querySelector('input[name="sort_by"]');
      var suffix = section.querySelector('.oddit-section-main-collection__sort-suffix');
      var current = input ? input.value : null;
      section.querySelectorAll('[data-sort-value]').forEach(function (option) {
        var selected = option.getAttribute('data-sort-value') === current;
        option.setAttribute('aria-selected', selected ? 'true' : 'false');
        if (selected && suffix) suffix.textContent = ': ' + option.textContent.trim();
      });
    }

    // Swapped-in product cards carry .oddit-reveal (opacity 0 until observed), and
    // the reveal IntersectionObserver only knows about nodes present at page load.
    // Re-arm the new subtree, or it renders blank.
    function refreshInteractions(root) {
      var interactions = window.OdditInteractions;
      if (interactions && typeof interactions.attach === 'function') {
        interactions.attach(root);
        return;
      }
      // Interactions bundle absent (theme setting off, or it failed to load):
      // reveal unconditionally rather than leave the grid invisible.
      root.querySelectorAll('.oddit-reveal:not(.is-visible)').forEach(function (el) {
        el.classList.add('is-visible');
      });
    }

    function syncApplyLabel(doc) {
      if (!applyBtn) return;
      var count = doc.querySelector('.oddit-section-main-collection__count');
      applyBtn.textContent = count ? 'Show ' + count.textContent.trim() : 'Apply filters';
    }

    function commit(doc, url, skipPush) {
      if (!doc) return;
      var incoming = doc.getElementById('shopify-section-' + sectionId) || doc.querySelector(SECTION_SELECTOR);
      if (!incoming) return;

      var incomingForm = incoming.querySelector('[data-filter-form]');
      if (form && incomingForm) {
        var accordion = readAccordionState();
        var focus = readFocus();
        form.innerHTML = incomingForm.innerHTML;
        applyAccordionState(accordion);
        restoreFocus(focus);
      }

      var incomingContent = incoming.querySelector('.oddit-section-main-collection__content');
      if (content && incomingContent) {
        content.innerHTML = incomingContent.innerHTML;
        refreshInteractions(content);
      }

      var incomingCount = incoming.querySelector('.oddit-section-main-collection__count');
      var count = section.querySelector('.oddit-section-main-collection__count');
      if (count && incomingCount) count.textContent = incomingCount.textContent;

      syncSortUi();
      syncApplyLabel(doc);
      pending = null;

      if (!skipPush) window.history.pushState({ odditFilters: true }, '', url);
    }

    // ---------- Apply ----------
    function applyNow(url) {
      var target = url || buildUrl();
      return fetchSection(target).then(function (doc) {
        commit(doc, target);
        return doc;
      });
    }

    // Desktop applies straight away. Mobile fetches but holds the result back
    // until Apply, so the grid behind the drawer never shifts underfoot.
    function changeFilters() {
      if (desktop.matches) return applyNow();
      var url = buildUrl();
      return fetchSection(url, true).then(function (doc) {
        if (!doc) return;
        pending = { doc: doc, url: url };
        syncApplyLabel(doc);
      });
    }

    // ---------- Price slider ----------
    function syncSlider(input) {
      var slider = input.closest('[data-price-slider]');
      var track = input.closest('.oddit-section-main-collection__slider-track');
      var max = parseFloat(input.max) || 0;
      var value = parseFloat(input.value) || 0;

      if (track) {
        track.style.setProperty('--oddit-slider-fill', (max ? (value / max) * 100 : 0) + '%');
      }
      var label = slider && slider.querySelector('[data-price-max]');
      if (label && slider) {
        label.textContent = formatMoney(value * 100, slider.getAttribute('data-money-format'));
      }
    }

    // ---------- Filter form ----------
    function initFilterForm() {
      if (!form) return;

      form.addEventListener('input', function (e) {
        if (e.target.matches('[data-price-max-input]')) syncSlider(e.target);
      });

      form.addEventListener('change', function (e) {
        if (e.target.matches('[data-price-max-input]')) {
          syncSlider(e.target);
          clearTimeout(priceTimer);
          priceTimer = setTimeout(changeFilters, PRICE_DEBOUNCE_MS);
          return;
        }
        if (e.target.matches('input[type="checkbox"]')) changeFilters();
      });

      // Only reachable via the <noscript> submit button, but a stray Enter in the
      // form would otherwise trigger a full navigation.
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        changeFilters();
      });
    }

    function initFilterGroups() {
      if (!sidebar) return;
      sidebar.addEventListener('click', function (e) {
        var heading = e.target.closest('[data-filter-heading]');
        if (!heading || !sidebar.contains(heading)) return;
        var group = heading.closest('.oddit-section-main-collection__filter-group');
        if (!group) return;
        var isOpen = group.getAttribute('data-open') === 'true';
        group.setAttribute('data-open', isOpen ? 'false' : 'true');
        heading.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      });
    }

    function initClearFilters() {
      if (!clearBtn) return;
      clearBtn.addEventListener('click', function (e) {
        if (!form) return; // no form: let the anchor navigate to the bare collection
        e.preventDefault();
        form.querySelectorAll('input[type="checkbox"]').forEach(function (checkbox) {
          checkbox.checked = false;
        });
        form.querySelectorAll('[data-price-max-input]').forEach(function (input) {
          input.value = input.getAttribute('data-price-range-max');
          syncSlider(input);
        });
        changeFilters();
      });
    }

    // ---------- Pagination ----------
    function initPagination() {
      if (!content) return;
      content.addEventListener('click', function (e) {
        var link = e.target.closest('.oddit-section-main-collection__page-num, .oddit-section-main-collection__page-arrow');
        if (!link || link.tagName !== 'A') return;
        var href = link.getAttribute('href');
        e.preventDefault();
        if (!href || href === '#') return; // the current page links to '#'
        applyNow(href).then(function (doc) {
          if (doc) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
    }

    // ---------- Sort ----------
    function initSortToggle() {
      var wrapper = section.querySelector('[data-sort]');
      if (!wrapper) return;
      var sortBtn = wrapper.querySelector('[data-sort-toggle]');
      var menu = wrapper.querySelector('[data-sort-menu]');
      if (!sortBtn || !menu) return;

      function setOpen(open) {
        sortBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
        menu.hidden = !open;
      }

      sortBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        setOpen(sortBtn.getAttribute('aria-expanded') !== 'true');
      });

      menu.querySelectorAll('[data-sort-value]').forEach(function (option) {
        option.addEventListener('click', function () {
          var value = option.getAttribute('data-sort-value');
          setOpen(false);

          if (!form) {
            // Filtering disabled: no form to carry sort_by, so rewrite the URL.
            var url = new URL(window.location.href);
            url.searchParams.set('sort_by', value);
            url.searchParams.delete('page');
            window.location.href = url.toString();
            return;
          }

          var input = form.querySelector('input[name="sort_by"]');
          if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'sort_by';
            form.appendChild(input);
          }
          input.value = value;
          // Sorting always commits, even on mobile — it is not part of the
          // drawer's deferred-apply flow.
          applyNow();
        });
      });

      document.addEventListener('click', function (e) {
        if (!wrapper.contains(e.target)) setOpen(false);
      });

      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape' || menu.hidden) return;
        setOpen(false);
        sortBtn.focus();
      });
    }

    // ---------- Sidebar / drawer ----------
    function initFilterToggle() {
      var filterBtn = section.querySelector('.oddit-section-main-collection__filter-toggle');
      if (!filterBtn || !sidebar) return;
      // Only the verb ("Hide "/"Show ") changes; it is hidden on mobile, where the
      // label reads just "Filters".
      var verb = filterBtn.querySelector('.oddit-section-main-collection__toggle-verb');

      function openDrawer() {
        // Snapshot so a close-without-apply can throw away uncommitted checkboxes.
        snapshot = form ? { html: form.innerHTML, label: applyBtn ? applyBtn.textContent : null } : null;
        sidebar.setAttribute('data-open', 'true');
        document.body.style.overflow = 'hidden';
      }

      function closeDrawer(discard) {
        if (sidebar.getAttribute('data-open') !== 'true') return;
        if (discard && snapshot && form) {
          cancelInFlight();
          requestSeq++; // discard whatever preview is still resolving
          setBusy(false, false);
          setBusy(false, true);
          form.innerHTML = snapshot.html;
          if (applyBtn && snapshot.label !== null) applyBtn.textContent = snapshot.label;
        }
        pending = null;
        snapshot = null;
        sidebar.removeAttribute('data-open');
        document.body.style.overflow = '';
      }

      filterBtn.addEventListener('click', function () {
        if (desktop.matches) {
          // Desktop: toggle the persistent filter column.
          var willHide = sidebar.getAttribute('data-hidden') !== 'true';
          sidebar.setAttribute('data-hidden', willHide ? 'true' : 'false');
          // --hide (dark, filled) state means the filters are currently shown.
          filterBtn.classList.toggle('oddit-section-main-collection__filter-toggle--hide', !willHide);
          if (verb) verb.textContent = willHide ? 'Show ' : 'Hide ';
        } else {
          openDrawer();
        }
      });

      if (applyBtn) {
        // A real submit button (for the no-JS path), so the default submit has to
        // go. This click fires before the form's submit event, which never runs.
        applyBtn.addEventListener('click', function (e) {
          e.preventDefault();
          if (pending) commit(pending.doc, pending.url);
          closeDrawer(false);
        });
      }

      section.querySelectorAll('[data-filter-close]').forEach(function (el) {
        el.addEventListener('click', function () {
          closeDrawer(true);
        });
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeDrawer(true);
      });

      // If the viewport grows to desktop while the drawer is open, drop the
      // overlay and apply what the shopper had staged — desktop has no Apply button.
      desktop.addEventListener('change', function (e) {
        if (!e.matches || sidebar.getAttribute('data-open') !== 'true') return;
        if (pending) commit(pending.doc, pending.url);
        closeDrawer(false);
      });
    }

    // ---------- History ----------
    window.addEventListener('popstate', function () {
      var url = window.location.href;
      fetchSection(url).then(function (doc) {
        commit(doc, url, true);
      });
    });

    document.addEventListener('shopify:section:unload', function (e) {
      if (!e.target.contains(section)) return;
      cancelInFlight();
      clearTimeout(priceTimer);
    });

    initFilterForm();
    initFilterGroups();
    initClearFilters();
    initPagination();
    initSortToggle();
    initFilterToggle();
  }

  document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });
})();
